import React, { useState, useCallback, useRef, useEffect } from 'react'
import { SealPercent, Tray, PlusCircle } from '@phosphor-icons/react'
import { CheckboxV2, Input, LinkButton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shopflo/ui'
import ColorPickerV2 from 'src/components/common/ColorPickerV2'
import { classNames } from 'src/lib/utils/helpers'
import { getDiscounts } from 'src/lib/core/api-methods'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import DiscountCreationDialog from 'src/components/experiments/experiments-creation-flow/components/DiscountCreationDialog'
import { ExperimentCreationOptions } from 'src/lib/types/experiments'
import { UiConfigDiscountType } from 'src/lib/types/discountsV2'
import { useExitNudge } from 'src/lib/contexts/ExitNudgeContext'

export type ExitNudgeOption = 'SURVEY' | 'EXIT_CONFIRMATION' | 'EXIT_COUPON_NUDGE' | 'URGENCY_NUDGE'

export interface ExitCouponConfig {
  selectedCoupon: string
  couponValue: string
  additionalSurvey: boolean
  badgeBackground: string
  badgeText: string
  badgeLabel: string
  nudgeText: string
  nudgeMessage: string
}

export interface UrgencyNudgeConfig {
  title: string
  textColor: string
  fomoMessage: string
}

export const URGENCY_PRESETS = ['Selling Fast', 'Sale ending soon', 'Prices may increase', 'Trusted by users']

const SectionDivider: React.FC<{ title: string }> = ({ title }) => (
  <div className='flex items-center gap-2 py-0.5'>
    <span className='text-text-sm-medium text-zinc-900 whitespace-nowrap'>{title}</span>
    <div className='flex-1 h-px bg-black/[0.06]' />
  </div>
)

export interface ExitCouponErrors {
  selectedCoupon?: string
  badgeLabel?: string
  nudgeMessage?: string
}

interface ExitCouponExpandedProps {
  config: ExitCouponConfig
  onChange: (config: ExitCouponConfig) => void
  canEdit: boolean
  errors?: ExitCouponErrors
}

const issuerId = process.env.NEXT_PUBLIC_APP_ISSUER_ID ?? ''

export const ExitCouponExpanded: React.FC<ExitCouponExpandedProps> = ({ config, onChange, canEdit, errors }) => {
  const { t } = useLocale()
  const {
    getters: { merchant }
  } = useMerchant()
  const accountId = merchant?.accountId

  const { couponExplicitlySelected, setCouponExplicitlySelected, exitCouponList, setExitCouponList, setIsCouponCreationOpen } = useExitNudge()
  const [isFetchingCoupon, setIsFetchingCoupon] = useState(false)
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const openCouponCreation = () => { setIsCreateDialogOpen(true); setIsCouponCreationOpen(true) }

  // Pre-fetch coupon list on mount so the saved selected coupon renders visibly in the dropdown
  useEffect(() => {
    if (config.selectedCoupon && exitCouponList.length === 0) {
      fetchDiscoveryCoupon()
    }
  }, [accountId])
  const closeCouponCreation = (open: boolean) => { setIsCreateDialogOpen(open); if (!open) setIsCouponCreationOpen(false) }
  const fetchingRef = useRef(false)

  const fetchDiscoveryCoupon = useCallback(async (): Promise<any[]> => {
    if (fetchingRef.current || !accountId) return []
    fetchingRef.current = true
    setIsFetchingCoupon(true)
    try {
      const res = await getDiscounts(issuerId, accountId, 10, 1, 'discounts', 'CART_DISCOVERY', '', undefined, 'EXIT_COUPON', '', 'ACTIVE')
      const coupons = res?.data?.data ?? []
      setExitCouponList(coupons)
      return coupons
    } catch {
      setExitCouponList([])
      return []
    } finally {
      fetchingRef.current = false
      setIsFetchingCoupon(false)
    }
  }, [accountId, setExitCouponList])


  const exitCoupons: { id: string; code: string; title: string; concessionAmount: number; amountType: string }[] =
    exitCouponList.map((c: any) => ({
      id: c.coupon_id,
      code: c.code,
      title: c.title || c.code,
      concessionAmount: c.concession_amount ?? 0,
      amountType: c.amount_type ?? 'FLAT'
    }))

  const formatCouponValue = (concessionAmount: number, amountType: string) =>
    amountType === 'PERCENTAGE' ? `${concessionAmount}%` : `₹${concessionAmount}`

  const handleCouponSelect = (id: string) => {
    const coupon = exitCoupons.find(c => c.id === id)
    if (!coupon) return
    setCouponExplicitlySelected(true)
    onChange({
      ...config,
      selectedCoupon: coupon.code,
      couponValue: formatCouponValue(coupon.concessionAmount, coupon.amountType)
    })
  }

  const selectedCoupon = exitCoupons.find(c => c.code === config.selectedCoupon)
  const selectedCouponId = selectedCoupon?.id || undefined
  // Show dependent sections only when the selected coupon is confirmed active:
  // either the user explicitly picked it this session, or it exists in the fetched active list
  const showCouponSections = Boolean(config.selectedCoupon) && (couponExplicitlySelected || Boolean(selectedCoupon))

  return (
  <div className='flex flex-col gap-4 border-b border-black/[0.06] pb- pr-16'>
    {/* Coupon select */}
    <div>
      <Select
        value={selectedCouponId}
        onValueChange={handleCouponSelect}
        disabled={!canEdit}
        open={isSelectOpen}
        onOpenChange={open => { setIsSelectOpen(open); if (open && exitCouponList.length === 0) fetchDiscoveryCoupon() }}
      >
        <SelectTrigger className={classNames('h-9 text-text-sm-regular', errors?.selectedCoupon ? 'border-red-500' : '')}>
          <div className='flex items-center gap-2'>
            <SealPercent size={16} className='text-zinc-500 flex-shrink-0' />
            {config.selectedCoupon && !selectedCouponId ? (
              <span className='text-text-sm-regular'>{config.selectedCoupon}</span>
            ) : (
              <SelectValue placeholder={t('exit_nudge.choose_exit_coupon')} />
            )}
          </div>
        </SelectTrigger>
        <SelectContent>
          {isFetchingCoupon ? (
            <SelectItem value='__loading__' disabled>
              {t('exit_nudge.loading_coupons')}
            </SelectItem>
          ) : exitCoupons.length === 0 ? (
            <div className='flex flex-col items-center justify-center gap-2 p-2 w-full h-[204px] rounded-xl bg-white shadow-dropdown'>
              <div className='flex flex-col items-center gap-4'>
                <Tray size={32} className='text-zinc-400' />
                <span className='text-text-sm-medium' style={{ color: 'var(--Text-subtext-darker, #52525C)' }}>
                  {t('exit_nudge.no_active_exit_coupons')}
                </span>
              </div>
              <LinkButton
                variant='primary'
                size='xs'
                leftIcon={<PlusCircle size={16} className='shrink-0' />}
                onClick={() => { setIsSelectOpen(false); openCouponCreation() }}
              >
                {t('exit_nudge.create_exit_coupon_now')}
              </LinkButton>
            </div>
          ) : (
            exitCoupons.map(coupon => (
              <SelectItem key={coupon.id} value={coupon.id}>
                {coupon.title}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {errors?.selectedCoupon && (
        <p className='mt-1 text-text-xs-regular text-red-600'>{errors.selectedCoupon}</p>
      )}
    </div>

    <DiscountCreationDialog
      type={ExperimentCreationOptions.EXIT_COUPONS}
      initialDiscountType={UiConfigDiscountType.EXIT_COUPON}
      isOpen={isCreateDialogOpen}
      setIsOpen={closeCouponCreation}
      setCreatedDiscount={() => {}}
      onSaveSuccess={async (createdDiscount: any) => {
        closeCouponCreation(false)
        fetchingRef.current = false
        const coupons = await fetchDiscoveryCoupon()
        const newCoupon = coupons.find((c: any) => c.coupon_id === createdDiscount?.coupon_id || c.code === createdDiscount?.code)
        if (newCoupon) {
          setCouponExplicitlySelected(true)
          onChange({
            ...config,
            selectedCoupon: newCoupon.code,
            couponValue: formatCouponValue(newCoupon.concession_amount ?? 0, newCoupon.amount_type ?? 'FLAT')
          })
        } else {
          setIsSelectOpen(true)
        }
      }}
    />

    {showCouponSections && (
      <>
        {/* Badge section */}
        <div className='flex flex-col gap-4'>
          <SectionDivider title={t('exit_nudge.badge')} />
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2'>
              <div className='w-[184.5px]'>
                <ColorPickerV2
                  label={t('exit_nudge.background')}
                  color={config.badgeBackground}
                  onChange={c => onChange({ ...config, badgeBackground: c })}
                  disabled={!canEdit}
                />
              </div>
              <div className='w-[184.5px]'>
                <ColorPickerV2
                  label={t('exit_nudge.text')}
                  color={config.badgeText}
                  onChange={c => onChange({ ...config, badgeText: c })}
                  disabled={!canEdit}
                />
              </div>
            </div>
            <Input
              label={t('exit_nudge.badge_label')}
              maxSize={30}
              dimension='md'
              value={config.badgeLabel}
              onChange={e => onChange({ ...config, badgeLabel: e.target.value })}
              placeholder={t('exit_nudge.badge_label_placeholder')}
              disabled={!canEdit}
              errorMessage={errors?.badgeLabel}
            />
          </div>
        </div>

        {/* Nudge section */}
        <div className='flex flex-col gap-4'>
          <SectionDivider title={t('exit_nudge.nudge')} />
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2'>
              <div className='w-[184.5px]'>
                <ColorPickerV2
                  label={t('exit_nudge.text')}
                  color={config.nudgeText}
                  onChange={c => onChange({ ...config, nudgeText: c })}
                  disabled={!canEdit}
                />
              </div>
            </div>
            <Input
              label={t('exit_nudge.nudge_message')}
              maxSize={30}
              dimension='md'
              value={config.nudgeMessage}
              onChange={e => onChange({ ...config, nudgeMessage: e.target.value })}
              placeholder={t('exit_nudge.nudge_message_placeholder')}
              disabled={!canEdit}
              errorMessage={errors?.nudgeMessage}
            />
          </div>
        </div>

        {/* Additional survey */}
        <div className='flex flex-row gap-2 items-start border-t border-black/[0.06] pt-3 pr-12'>
          <div className='mt-0.5'>
            <CheckboxV2
              size='sm'
              checked={config.additionalSurvey}
              onCheckedChange={checked => onChange({ ...config, additionalSurvey: Boolean(checked) })}
              disabled={!canEdit}
            />
          </div>
          <div
            className={classNames('flex flex-col', canEdit ? 'cursor-pointer' : 'cursor-not-allowed')}
            onClick={() => canEdit && onChange({ ...config, additionalSurvey: !config.additionalSurvey })}
          >
            <span className='text-text-sm-medium text-zinc-900'>{t('exit_nudge.additional_survey')}</span>
            <span className='text-text-xs-regular text-zinc-500'>{t('exit_nudge.additional_survey_subtitle')}</span>
          </div>
        </div>
      </>
    )}
  </div>
  )
}

export interface UrgencyNudgeErrors {
  title?: string
  fomoMessage?: string
}

interface UrgencyNudgeExpandedProps {
  config: UrgencyNudgeConfig
  onChange: (config: UrgencyNudgeConfig) => void
  canEdit: boolean
  errors?: UrgencyNudgeErrors
}

export const UrgencyNudgeExpanded: React.FC<UrgencyNudgeExpandedProps> = ({ config, onChange, canEdit, errors }) => {
  const { t } = useLocale()

  return (
    <div className='flex flex-col gap-3 pr-16 pb-2'>
      <Input
        label={t('exit_nudge.urgency_title_label')}
        maxSize={30}
        dimension='md'
        value={config.title}
        onChange={e => onChange({ ...config, title: e.target.value })}
        placeholder={t('exit_nudge.urgency_title_placeholder')}
        disabled={!canEdit}
        errorMessage={errors?.title}
      />

      {/* Preset suggestion chips */}
      <div className='flex flex-row flex-wrap gap-1.5'>
        {URGENCY_PRESETS.map(preset => (
          <button
            key={preset}
            type='button'
            disabled={!canEdit}
            onClick={() => onChange({ ...config, title: preset })}
            className={classNames(
              'px-2 py-0.5 text-text-xs-regular rounded bg-gray-100 text-zinc-700',
              'hover:bg-gray-200 active:bg-gray-300 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
          >
            {preset}
          </button>
        ))}
      </div>

      <div className='flex flex-col gap-3 border-t border-black/[0.06] pt-3'>
        <ColorPickerV2
          label={t('exit_nudge.text')}
          color={config.textColor}
          onChange={c => onChange({ ...config, textColor: c })}
          disabled={!canEdit}
        />
        <Input
          label={t('exit_nudge.fomo_message')}
          maxSize={60}
          dimension='md'
          value={config.fomoMessage}
          onChange={e => onChange({ ...config, fomoMessage: e.target.value })}
          placeholder={t('exit_nudge.fomo_message_placeholder')}
          disabled={!canEdit}
          errorMessage={errors?.fomoMessage}
        />
      </div>
    </div>
  )
}
